import { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Lock } from '@phosphor-icons/react';

/**
 * Owner password verification dialog.
 * - For per-trip verification: pass `tripCode` prop → calls POST /trips/{code}/verify-owner
 * - For platform creation gate: omit `tripCode` → calls POST /auth/verify-owner
 * Returns token via onSuccess(token).
 */
export default function OwnerPasswordDialog({ open, onOpenChange, onSuccess, description, tripCode }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      let token;
      if (tripCode) {
        // Per-trip owner verification
        const { data } = await api.post(`/trips/${tripCode}/verify-owner`, { password: password.trim() });
        token = data.token;
      } else {
        // Platform creation gate
        const { data } = await api.post('/auth/verify-owner', { password: password.trim() });
        token = data.gate_active ? data.token : '__disabled__';
      }
      setPassword('');
      onSuccess(token);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'trip_owner_password_not_set') {
        setError('No master password set for this event. Ask the organiser to set one.');
      } else {
        setError(detail || 'Invalid password');
      }
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) setPassword(''); onOpenChange(val); }}>
      <DialogContent className="sm:max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#1B4332]" weight="bold" />
            Master Password Required
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#455561]">{description || 'Enter the master password to continue.'}</p>
        <form onSubmit={handleSubmit} className="space-y-3 mt-1">
          <Input type="password" placeholder="Master password" value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            className="h-12 rounded-xl text-base" autoFocus data-testid="owner-password-input" />
          {error && <p className="text-sm text-[#D32F2F]" data-testid="owner-password-error">{error}</p>}
          <DialogFooter className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-xl">Cancel</Button>
            <Button type="submit" disabled={loading || !password.trim()}
              className="flex-1 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl" data-testid="owner-password-submit">
              {loading ? 'Verifying...' : 'Continue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
